import { useCallback, useMemo, useState } from "react";

import startCase from "lodash/startCase";
import moment from "moment";

import {
  Button,
  Link,
  Page,
  Spinner,
  Text,
  toastError,
  toastInfo,
  toastSuccess,
  useModal,
} from "@alpacahq/ui/old";

import { useGetOrder } from "~/v2/api/hooks/useGetOrder";
import {
  cancelOrder,
  type Order,
  type OrderLeg,
  replaceOrder,
} from "~/v2/api/rest/account";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { Path } from "../../path";
import { type ReplaceOrderDetails, ReplaceOrderModal } from "./ReplaceOrder";

const dateFormat = "YYYY-MM-DDTHH:mm:ss.SSSSSSZ";

function isDateEntry(entry: [string, unknown]): entry is [string, string] {
  return (
    typeof entry[1] === "string" && moment(entry[1], dateFormat, true).isValid()
  );
}

const OrderContainer = () => {
  const { currentAccount, product } = useAccountContext();

  // order replace modal
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  // parse orderID from url path
  const paths = window.location.pathname.split("/");
  const targetId = paths[paths.length - 1];

  const {
    order,
    isLoading: orderIsLoading,
    refetch: refetchOrder,
  } = useGetOrder({
    accountID: currentAccount?.id,
    product,
    orderId: targetId,
    options: {
      enabled: !!currentAccount?.id && !!targetId,
      retry: false,
    },
  });

  const canReplace =
    order && (order.status === "new" || order.status === "partially_filled");

  // order history
  const orderHistory = useMemo(
    () =>
      Object.entries(order ?? {})
        .filter(isDateEntry)
        .sort((a, b) => a[1].localeCompare(b[1])),
    [order],
  );

  // cancelling order
  const orderCancel = useCallback(
    () =>
      cancelOrder({
        product: product ?? "",
        accountID: currentAccount?.id ?? "",
        orderID: order?.id ?? "",
      })
        .then(() => {
          refetchOrder();
          toastSuccess("Order successfully cancelled");
        })
        .catch((e) => {
          console.error(e);
          toastError(
            "Error: Could not cancel order. There was a problem trying to cancel your order. Please try again.",
          );
        }),
    [order, currentAccount?.id, product, refetchOrder],
  );

  // replace order
  const orderReplace = useCallback(
    (toReplace: Order, orderDetails: ReplaceOrderDetails) => {
      // type coercion for check
      const isUnchanged =
        orderDetails.qty === toReplace.qty &&
        orderDetails.time_in_force === toReplace.time_in_force &&
        orderDetails.limit_price === toReplace.limit_price &&
        orderDetails.stop_price === toReplace.stop_price &&
        orderDetails.trail === toReplace.trail_price;

      if (isUnchanged) {
        setIsReplaceModalOpen(false);
        toastInfo(
          "Order unchanged: Update order fields to replace the current order",
          {},
        );
        return;
      }

      replaceOrder({
        product: product ?? "",
        accountID: currentAccount?.id ?? "",
        orderID: toReplace.id,
        orderDetails,
      })
        .then(() => {
          refetchOrder();
          toastSuccess("Order successfully replaced");
        })
        .catch((e) => {
          console.error(e);
          toastError(
            "Error: Could not replace order. There was a problem trying to replace your order. Please try again.",
          );
        })
        .finally(() => setIsReplaceModalOpen(false));
    },
    [currentAccount?.id, product, refetchOrder],
  );

  const cancelModal = useModal({
    title: "Cancel Order",
    content: "Are you sure you want to cancel this order?",
    controls: (
      <div className="flex items-center justify-between gap-4">
        <Button
          type="button"
          onClick={() => cancelModal.toggle()}
          variant="secondary"
        >
          No
        </Button>
        <Button
          type="button"
          onClick={() => {
            orderCancel();
            cancelModal.toggle();
          }}
        >
          Yes
        </Button>
      </div>
    ),
  });

  const orderCancelConfirm = () => {
    cancelModal.toggle();
  };

  // if not found return error message
  if (!orderIsLoading && !order) {
    return (
      <Page title="Order Not Found">
        <div className="flex flex-col gap-4">
          <h2 className="font-medium text-lg">
            Sorry, we could not find order {targetId}
          </h2>
          <Link to={Path.ROUTE_ORDERS} className="block">
            ← Back to Orders
          </Link>
        </div>
      </Page>
    );
  }

  const isMultileg = order?.order_class === "mleg";
  const sharesMsg = isMultileg
    ? `${order?.legs?.length} Legs`
    : order?.notional
      ? ` $${order?.notional}`
      : ` ${order?.qty} ${
          order?.asset_class === "us_option" ? "Contracts" : "Shares"
        }`;

  const isLoading = !order;

  return (
    <Page
      title={isLoading ? "" : isMultileg ? "Multi-leg" : order.symbol}
      subtitle={isLoading ? undefined : `Order ${order.id}`}
    >
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Link to={Path.ROUTE_ORDERS} className="block">
            ← Back to Orders
          </Link>

          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="flex flex-col gap-2 pl-2">
                <Text className="font-bold text-lg">
                  {isMultileg ? "Multi-leg" : order.symbol}
                  {order.asset_class === "crypto" && "CRYPTO"}
                </Text>
                <Text>
                  {`${startCase(order.type)} ${startCase(order.side)}`}
                  <span className="text-gray-600">{sharesMsg}</span>
                </Text>
              </div>

              <div className="flex flex-wrap gap-2">
                {canReplace && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setIsReplaceModalOpen(true)}
                    >
                      Replace Order
                    </Button>
                    <Button variant="red" onClick={orderCancelConfirm}>
                      Cancel Order
                    </Button>
                  </>
                )}
              </div>
            </div>

            {isMultileg ? (
              <MultilegDetailsCard focusedOrder={order} />
            ) : (
              <OrderDetailsCard focusedOrder={order} />
            )}
            {order.legs?.map((leg) => (
              <div
                key={leg.id}
                className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4"
              >
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="flex flex-col gap-2">
                    <Text className="font-bold text-lg">{leg.symbol}</Text>
                  </div>
                </div>
                <OrderLegCard leg={leg} />
              </div>
            ))}

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
                <div className="flex flex-col gap-2 overflow-x-auto">
                  <Text className="text-gray-600">Lifecycle of Status</Text>
                  <div className="flex flex-col">
                    {orderHistory.map((hist) => (
                      <div
                        key={`${hist[0]}-${hist[1]}`}
                        className="grid grid-cols-2 gap-2"
                      >
                        <Text className="font-medium text-gray-900">
                          {startCase(hist[0].split("_").join(" "))}
                        </Text>
                        <Text className="font-medium text-gray-900">
                          {hist[1]}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2 overflow-x-auto">
                  <Text className="text-gray-600">Order Linkages</Text>
                  <div className="flex flex-col">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col">
                        <Text className="font-medium text-gray-900">
                          Replaces
                        </Text>
                        <Text className="font-medium text-gray-900">
                          Replaced By
                        </Text>
                      </div>
                      <div className="flex flex-col">
                        {(
                          [
                            { role: "replaces", oid: order.replaces },
                            { role: "replaced_by", oid: order.replaced_by },
                          ] as const
                        ).map(({ role, oid }) =>
                          oid ? (
                            <Link
                              key={role}
                              to={Path.format(Path.ROUTE_ORDER_DETAIL, {
                                orderId: oid,
                              })}
                              className="block font-medium text-gray-900"
                            >
                              {oid}
                            </Link>
                          ) : (
                            <Text
                              key={role}
                              className="font-medium text-gray-900"
                            >
                              -
                            </Text>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isReplaceModalOpen && order && (
        <ReplaceOrderModal
          isOpen={isReplaceModalOpen}
          onClose={() => setIsReplaceModalOpen(false)}
          onConfirm={(orderDetails) => orderReplace(order, orderDetails)}
          order={order}
        />
      )}

      {cancelModal.component}
    </Page>
  );
};

const MultilegDetailsCard = ({ focusedOrder }: { focusedOrder: Order }) => {
  // coloring on order status
  const statusStyle = { color: "" };
  if (focusedOrder.status === "filled") {
    statusStyle.color = "green";
  }
  if (focusedOrder.status === "rejected") {
    statusStyle.color = "red";
  }
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-gray-600">Order ID</div>
            <div className="font-medium text-gray-900">{focusedOrder.id}</div>
          </div>
          <div>
            <div className="text-gray-600">Status</div>
            <div className="font-medium text-gray-900" style={statusStyle}>
              {focusedOrder.status}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Order Type</div>
            <div className="font-medium text-gray-900">{focusedOrder.type}</div>
          </div>
          <div>
            <div className="text-gray-600">Quantity</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.qty ?? "-"}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Time In Force</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.time_in_force}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-gray-600">Extended Hours</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.extended_hours ? "Yes" : "No"}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Limit Price</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.limit_price || "-"}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Created At</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.created_at}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Submitted At</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.submitted_at}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderDetailsCard = ({ focusedOrder }: { focusedOrder: Order }) => {
  // coloring on order status
  const statusStyle = { color: "" };
  if (focusedOrder.status === "filled") {
    statusStyle.color = "green";
  }
  if (focusedOrder.status === "rejected") {
    statusStyle.color = "red";
  }
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-gray-600">Order ID</div>
            <div className="font-medium text-gray-900">{focusedOrder.id}</div>
          </div>
          <div>
            <div className="text-gray-600">Status</div>
            <div className="font-medium text-gray-900" style={statusStyle}>
              {focusedOrder.status}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Symbol</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.symbol}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Side</div>
            <div className="font-medium text-gray-900">{focusedOrder.side}</div>
          </div>
          <div>
            <div className="text-gray-600">Order Type</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.order_type}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Quantity</div>
            <div className="font-medium text-gray-900">{focusedOrder.qty}</div>
          </div>
          <div>
            <div className="text-gray-600">Time In Force</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.time_in_force}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-gray-600">Extended Hours</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.extended_hours ? "Yes" : "No"}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Limit Price</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.limit_price || "-"}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Stop Price</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.stop_price || "-"}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Created At</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.created_at}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Submitted At</div>
            <div className="font-medium text-gray-900">
              {focusedOrder.submitted_at}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderLegCard = ({ leg }: { leg: OrderLeg }) => {
  // coloring on order status
  const statusStyle = { color: "" };
  if (leg.status === "filled") {
    statusStyle.color = "green";
  }
  if (leg.status === "rejected") {
    statusStyle.color = "red";
  }
  return (
    <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
      <div className="flex flex-col gap-2">
        <div>
          <div className="text-gray-600">Order Leg ID</div>
          <div className="font-medium text-gray-900">{leg.id}</div>
        </div>
        <div>
          <div className="text-gray-600">Status</div>
          <div className="font-medium text-gray-900" style={statusStyle}>
            {leg.status}
          </div>
        </div>
        <div>
          <div className="text-gray-600">Symbol</div>
          <div className="font-medium text-gray-900">{leg.symbol}</div>
        </div>
        <div>
          {" "}
          <div className="text-gray-600">Side</div>
          <div className="font-medium text-gray-900">{leg.side}</div>
        </div>
        <div>
          <div className="text-gray-600">Position Intent</div>
          <div className="font-medium text-gray-900">{leg.position_intent}</div>
        </div>
        <div>
          <div className="text-gray-600">Quantity</div>
          <div className="font-medium text-gray-900">{leg.qty}</div>
        </div>
        <div>
          <div className="text-gray-600">Ratio Quantity</div>
          <div className="font-medium text-gray-900">{leg.ratio_qty}</div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div>
          <div className="text-gray-600">Time In Force</div>
          <div className="font-medium text-gray-900">{leg.time_in_force}</div>
        </div>
        <div>
          <div className="text-gray-600">Extended Hours</div>
          <div className="font-medium text-gray-900">
            {leg.extended_hours ? "Yes" : "No"}
          </div>
        </div>
        <div>
          <div className="text-gray-600">Created At</div>
          <div className="font-medium text-gray-900">{leg.created_at}</div>
        </div>
        <div>
          <div className="text-gray-600">Submitted At</div>
          <div className="font-medium text-gray-900">{leg.submitted_at}</div>
        </div>
      </div>
    </div>
  );
};

export default OrderContainer;
